import {useCallback, useEffect, useMemo, useState} from 'react';
import {t} from '../../../../common/getTranslations';
import {getDataset} from '../../../../common/shared/utils/getDataset';
import {twMerge} from '../../../../common/twMerge';
import {Loading} from '../../../../design-system/atoms/Loading/Loading';
import {Text} from '../../../../design-system/atoms/Text/Text';
import {ContentHomeNoticeLineBanners} from '../../../../design-system/organisms/ContentHomeNoticeLineBanners/ContentHomeNoticeLineBanners';
import {ContentNoticeItem} from '../../../../design-system/organisms/ContentNoticeItem/ContentNoticeItem';
import {Pagination} from '../../../../design-system/organisms/Pagination/Pagination';
import {useContentHomeNoticeQuery} from '../../../../wrapper/contentWrapper';
import {useContentHomeLastNotice} from '../hook/useContentHomeLastNotice';
import {scrollToWindowTopmost} from '../viewer/[productId]/util/scrollToWindowTopmost';

export function ContentNoticeTabPcListContainer({
  seriesId,
}: {
  seriesId: number;
}) {
  const firstPageData = useContentHomeNoticeQuery({
    contentHomeNoticeInput: {
      seriesId,
      page: 0,
    },
  }).data?.contentHomeNotice;

  const {visit} = useContentHomeLastNotice(seriesId);
  useEffect(() => {
    visit();
  }, [visit]);

  const [page, setPage] = useState(0);
  const {isFetching: isMoreLoading, data} = useContentHomeNoticeQuery({
    contentHomeNoticeInput: {
      seriesId,
      page,
    },
  });
  const noticeData = data?.contentHomeNotice;

  const onChangePage = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (!firstPageData) {
        return;
      }
      const newPage = Number(getDataset(e.currentTarget, 'page')) - 1;
      if (page === newPage) {
        return;
      }
      setPage(newPage);
      scrollToWindowTopmost();
    },
    [firstPageData, page]
  );

  const noticeDataInfo = useMemo(() => {
    if (firstPageData?.topNotice) {
      return {
        topNotice: firstPageData.topNotice,
        noticeList: noticeData?.noticeListContainer?.noticeList,
        noItem: false,
      };
    }

    if (
      noticeData?.noticeListContainer?.noticeList &&
      noticeData.noticeListContainer.noticeList.length > 0
    ) {
      return {
        topNotice: noticeData.noticeListContainer.noticeList[0],
        noticeList: noticeData.noticeListContainer.noticeList.slice(1),
        noItem: false,
      };
    }

    return {
      noItem: true,
    };
  }, [noticeData?.noticeListContainer?.noticeList, firstPageData?.topNotice]);

  return (
    <>
      {(noticeDataInfo?.topNotice ||
        (firstPageData?.lineBannerList &&
          firstPageData.lineBannerList.length !== 0)) && (
        <div
          className={twMerge(
            'flex w-full flex-col overflow-hidden bg-theme-bg-surface-alt',
            noticeDataInfo?.topNotice && 'rounded-b-12pxr'
          )}
        >
          <ContentHomeNoticeLineBanners
            isMobile={false}
            lineBannerList={firstPageData?.lineBannerList}
          />
          {noticeDataInfo?.topNotice && (
            <ContentNoticeItem
              type={noticeDataInfo?.topNotice.noticeType}
              isPinned={!!firstPageData?.topNotice}
              noticeTitle={noticeDataInfo?.topNotice.title}
              noticeDescription={noticeDataInfo?.topNotice.description}
              createDate={noticeDataInfo?.topNotice.createDate}
            />
          )}
        </div>
      )}
      <div
        className={twMerge(
          'flex w-full flex-col space-y-4pxr',
          noticeDataInfo.noItem && 'bg-theme-bg-surface-alt'
        )}
      >
        {isMoreLoading && (
          <div className="flex-center-full">
            <Loading />
          </div>
        )}
        {!isMoreLoading && noticeDataInfo.noItem && (
          <div className="min-h-[656px] flex-center-full">
            <Text className="font-medium1 text-theme-solid-100">
              {t('contenthome_notice_tab_empty')}
            </Text>
          </div>
        )}
        {!isMoreLoading &&
          !noticeDataInfo.noItem &&
          noticeDataInfo?.noticeList &&
          noticeDataInfo.noticeList.length > 0 && (
            <div className="mt-4pxr flex flex-col space-y-4pxr">
              {noticeDataInfo.noticeList.map(notice => {
                return (
                  <ContentNoticeItem
                    key={notice.id}
                    type={notice.noticeType}
                    isPinned={false}
                    noticeTitle={notice.title}
                    noticeDescription={notice.description}
                    createDate={notice?.createDate}
                  />
                );
              })}
              <Pagination
                currentPage={(page || 0) + 1}
                contentsPerPage={25}
                pagingSize={10}
                onChangePage={onChangePage}
                totalCount={firstPageData?.noticeListContainer?.totalCount || 0}
              />
            </div>
          )}
      </div>
    </>
  );
}
